/**
 * Portal Manta · RetigSchematic
 * Extraído de App.jsx v29.3 (via AST)
 */
import React, { useState, useEffect, useMemo, useCallback, Fragment, memo } from 'react';
import { V, S, OBRAS_ROD } from '../../globals.js';

function RetigSchematic({rodovia, obrasRod}){
  const ext = rodovia?.ext||0;
  const W=900, H=260, mLeft=70, mRight=20, lineY=H/2;
  const usable = W - mLeft - mRight;
  // categorias: linear (km) acima, pontual (n) abaixo
  const linear = [
    {k:'duplicacoes', l:'Duplicação',  color:'#1d4ed8', kmField:'km'},
    {k:'faixas',      l:'Faixa Adic.', color:'#ea580c', kmField:'km'},
    {k:'marginais',   l:'Marginais',   color:'#16a34a', kmField:'km'},
    {k:'ciclovias',   l:'Ciclovias',   color:'#db2777', kmField:'km'},
    {k:'contornos',   l:'Contornos',   color:'#7c3aed', kmField:'km'},
  ];
  const pontual = [
    {k:'oaes',       l:'OAE',         color:'#7c3aed', sym:'◆'},
    {k:'passarelas', l:'Passarela',   color:'#0891b2', sym:'║'},
    {k:'diamantes',  l:'Diamante',    color:'#f59e0b', sym:'◇'},
    {k:'trombetas',  l:'Trombeta',    color:'#84cc16', sym:'△'},
    {k:'parclos',    l:'Parclo',      color:'#10b981', sym:'○'},
    {k:'rotatorias', l:'Rotatória',   color:'#94a3b8', sym:'◯'},
  ];
  // Coletar dados
  const linDados = linear.map((c,idx)=>{
    const items = (obrasRod[c.k]||[]).filter(x=>x.rod===rodovia.rod);
    const totalKm = items.reduce((s,x)=>s+(x[c.kmField]||0),0);
    return {...c, items, totalKm, idx};
  }).filter(d=>d.totalKm>0);
  const pntDados = pontual.map(c=>{
    const items = (obrasRod[c.k]||[]).filter(x=>x.rod===rodovia.rod);
    const total = items.reduce((s,x)=>s+(x.n||1),0);
    return {...c, items, total};
  }).filter(d=>d.total>0);
  // se nada, retorna placeholder
  if(linDados.length===0 && pntDados.length===0){
    return(<div style={{...S.cardP,fontSize:11,color:V.gr,fontStyle:'italic'}}>Sem segmentos cadastrados em OBRAS_ROD para {rodovia.rod}.</div>);
  }
  const trackH = 14;
  const trackGap = 4;
  const upTracks = linDados.length;
  const downTracks = pntDados.length>0 ? 1 : 0;
  const totalH = (upTracks*(trackH+trackGap)) + 50 + (downTracks*(trackH+trackGap)) + 30;
  const lineYY = upTracks*(trackH+trackGap) + 30;
  // ticks de km
  const ticks = [];
  const tickStep = ext>200?50:(ext>50?20:10);
  for(let k=0;k<=ext;k+=tickStep) ticks.push(k);
  if(ticks[ticks.length-1] !== Math.round(ext)) ticks.push(Math.round(ext));
  return(<div style={{...S.cardP,overflow:'auto'}}>
    <svg width={W} height={totalH} style={{minWidth:W,fontFamily:'-apple-system,sans-serif'}}>
      {/* Linhas verticais de km grid */}
      {ticks.map(k=>{
        const x = mLeft + (k/ext)*usable;
        return(<g key={k}>
          <line x1={x} y1={20} x2={x} y2={totalH-10} stroke="#e5e7eb" strokeWidth={1} strokeDasharray="2,2"/>
          <text x={x} y={15} fontSize={9} fill="#64748b" textAnchor="middle">{k}km</text>
        </g>);
      })}
      {/* Tracks lineares (acima da linha) */}
      {linDados.map((d,i)=>{
        const ty = 20 + 10 + i*(trackH+trackGap);
        // Distribui os trechos em posições esquemáticas (consecutivos)
        let cursor = 0;
        return(<g key={d.k}>
          <text x={mLeft-6} y={ty+trackH-3} fontSize={9.5} fill="#475569" textAnchor="end" fontWeight={600}>{d.l}</text>
          {d.items.map((it,j)=>{
            const km = it[d.kmField]||0;
            const x = mLeft + (cursor/ext)*usable;
            const w = (km/ext)*usable;
            cursor += km;
            return(<g key={j}>
              <rect x={x} y={ty} width={w} height={trackH} fill={d.color} fillOpacity={0.85} stroke={d.color} strokeWidth={0.5}/>
              {w>40 && <text x={x+w/2} y={ty+trackH/2+3} fontSize={9} fill="#fff" textAnchor="middle" fontWeight={600}>{km.toFixed(1)} km</text>}
            </g>);
          })}
          <text x={W-mRight+4} y={ty+trackH-3} fontSize={9} fill={d.color} fontWeight={700}>Σ {d.totalKm.toFixed(1)} km</text>
        </g>);
      })}
      {/* Linha principal da rodovia */}
      <line x1={mLeft} y1={lineYY} x2={W-mRight} y2={lineYY} stroke="#0f172a" strokeWidth={5}/>
      <text x={mLeft-8} y={lineYY+4} fontSize={11} fill="#0f172a" textAnchor="end" fontWeight={700}>{rodovia.rod}</text>
      <text x={W-mRight+8} y={lineYY+4} fontSize={9.5} fill="#0f172a" fontWeight={700}>{ext.toFixed(1)} km</text>
      {/* Pontuais (abaixo da linha) — distribuídos esquematicamente */}
      {pntDados.length>0 && (()=>{
        const py = lineYY + 22;
        // total de marcadores
        const allMarks = [];
        pntDados.forEach(d=>{
          for(let i=0;i<d.total;i++) allMarks.push({...d, _i:i});
        });
        return allMarks.map((m,idx)=>{
          const x = mLeft + ((idx+0.5)/allMarks.length)*usable;
          return(<g key={idx}>
            <line x1={x} y1={lineYY} x2={x} y2={py} stroke={m.color} strokeWidth={1}/>
            <circle cx={x} cy={py+5} r={5} fill={m.color}/>
            <text x={x} y={py+8.5} fontSize={7} fill="#fff" textAnchor="middle" fontWeight={700}>{m.l[0]}</text>
          </g>);
        });
      })()}
      {/* Legenda dos pontuais */}
      {pntDados.length>0 && pntDados.map((d,i)=>{
        const lx = mLeft + i*120;
        const ly = totalH-15;
        return(<g key={d.k}>
          <circle cx={lx} cy={ly-3} r={5} fill={d.color}/>
          <text x={lx+9} y={ly} fontSize={9} fill="#475569" fontWeight={600}>{d.l} ({d.total})</text>
        </g>);
      })}
    </svg>
  </div>);
}

export { RetigSchematic };
export default RetigSchematic;
